import React, { useEffect, useState, useRef } from "react";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, Crosshair, Loader2 } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { toast } from "sonner";

const DEFAULT_CENTER = [25.2048, 55.2708]; // Dubai

// Fix default Leaflet marker icon (uses CDN to avoid bundler issues)
const goldIcon = L.divIcon({
  className: "m16-marker",
  html: `<div style="width:34px;height:34px;border-radius:50% 50% 50% 0;background:#D4AF37;border:3px solid #0A0A0B;box-shadow:0 6px 18px rgba(212,175,55,0.5);transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;">
           <div style="width:10px;height:10px;border-radius:50%;background:#0A0A0B;transform:rotate(45deg);"></div>
         </div>`,
  iconSize: [34, 34],
  iconAnchor: [17, 30],
});

const ClickHandler = ({ onPick }) => {
  useMapEvents({ click(e) { onPick(e.latlng.lat, e.latlng.lng); } });
  return null;
};

const Recenter = ({ position }) => {
  const map = useMap();
  useEffect(() => {
    if (position) map.setView(position, Math.max(map.getZoom(), 14));
  }, [position, map]);
  return null;
};

// Free reverse geocoding via OpenStreetMap Nominatim
async function reverseGeocode(lat, lng, lang) {
  try {
    const url = `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&accept-language=${lang}`;
    const res = await fetch(url, { headers: { Accept: "application/json" } });
    const data = await res.json();
    return data.display_name || "";
  } catch { return ""; }
}

export const LocationPicker = ({ value, onChange }) => {
  const { t, lang } = useLanguage();
  const [busy, setBusy] = useState(false);
  const [pos, setPos] = useState(value?.lat ? [value.lat, value.lng] : null);
  const mountedRef = useRef(true);

  useEffect(() => { return () => { mountedRef.current = false; }; }, []);

  const pick = async (lat, lng) => {
    setPos([lat, lng]);
    setBusy(true);
    const address = await reverseGeocode(lat, lng, lang);
    if (!mountedRef.current) return;
    setBusy(false);
    onChange?.({ lat, lng, address });
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported");
      return;
    }
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      (p) => pick(p.coords.latitude, p.coords.longitude),
      () => { setBusy(false); toast.error(lang === "ar" ? "تعذّر الوصول للموقع" : "Cannot access location"); },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  return (
    <div className="space-y-3" data-testid="location-picker">
      <div className="flex items-center justify-between">
        <label className="text-sm text-[#9CA3AF] inline-flex items-center gap-2">
          <MapPin size={14} className="text-[#D4AF37]" />
          {lang === "ar" ? "حدّد موقع منزلك على الخريطة" : "Pick your home location on the map"}
        </label>
        <button
          type="button"
          onClick={useMyLocation}
          data-testid="use-my-location-btn"
          className="text-xs inline-flex items-center gap-1.5 text-[#D4AF37] hover:opacity-80"
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Crosshair size={12} />}
          {lang === "ar" ? "موقعي الحالي" : "Use my location"}
        </button>
      </div>

      <div className="rounded-xl overflow-hidden border border-[#27272A]" style={{ height: 280 }}>
        <MapContainer center={pos || DEFAULT_CENTER} zoom={pos ? 14 : 11} className="w-full h-full" style={{ background: "#121214" }}>
          <TileLayer
            attribution='&copy; OpenStreetMap'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <ClickHandler onPick={pick} />
          {pos && <Marker position={pos} icon={goldIcon} />}
          {pos && <Recenter position={pos} />}
        </MapContainer>
      </div>

      {value?.address && (
        <div className="text-xs bg-[#121214] border border-[#27272A] rounded-lg p-3 text-[#F3F4F6]/85 leading-relaxed" data-testid="picked-address">
          <span className="text-[#D4AF37]">📍 </span>{value.address}
        </div>
      )}
    </div>
  );
};

export default LocationPicker;
